// Shared helpers for Config Strip components (ordering + attribute extraction)

import { ScaleSets } from 'graphql-api/gql-api-interfaces';
import { MaterialWidthIds } from '@epicuro-next/models/products';
import { Product } from 'graphql-api/gql-api-interfaces';

/** Normalize rule value into a string array (supports CSV string or string[]) */
export function normalizeOrderInput(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((s) => `${s}`.trim());
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Stable, case-insensitive sort by a preferred order.
 * Items not present in the order keep their original relative order,
 * after items that DO have a rank.
 *
 * "ranked stuff": we map each preferred value (lowercased) to a rank index,
 * then sort by that rank; unranked values get +∞ and fall back to original order.
 */
export function sortByOrderStable<T>(
  items: T[],
  getKey: (t: T) => string,
  order: string[],
): T[] {
  if (!Array.isArray(items) || items.length <= 1 || !order?.length)
    return items;

  const norm = (s: string) => s?.toString().trim().toLowerCase();
  const rank = new Map(order.map((val, idx) => [norm(val), idx]));
  const originalIndex = new Map(items.map((v, i) => [v, i]));

  const copy = [...items];
  copy.sort((a, b) => {
    const ra = rank.has(norm(getKey(a)))
      ? (rank.get(norm(getKey(a))) as number)
      : Number.POSITIVE_INFINITY;
    const rb = rank.has(norm(getKey(b)))
      ? (rank.get(norm(getKey(b))) as number)
      : Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    return (originalIndex.get(a) as number) - (originalIndex.get(b) as number);
  });

  return copy;
}

/**
 * Return a new ScaleSets with the SECOND dimension (Y) re-ordered
 * for a given scale key using the provided order. X dimension is left intact.
 * No mutation to the original.
 */
export function reorderSecondDimensionInScaleSets(
  scaleSets: ScaleSets,
  yOrder: string[],
): ScaleSets {
  if (!scaleSets?.scales || !yOrder?.length) return scaleSets;

  const scaleKeys = Object.keys(scaleSets.scales);
  if (!scaleKeys.length) return scaleSets;

  const reorderedScales = scaleKeys.reduce((acc, key) => {
    const source = scaleSets.scales[key];
    const dims = Object.keys(source ?? {});
    if (dims.length < 2) {
      acc[key] = source;
      return acc;
    }

    const xDim = dims[0];
    const yDim = dims[1];
    const before = source[yDim] ?? [];
    const after = sortByOrderStable(before, (s) => `${s}`, yOrder);

    acc[key] = { ...source, [yDim]: after, [xDim]: source[xDim] };
    return acc;
  }, {} as ScaleSets['scales']);

  return {
    ...scaleSets,
    scales: reorderedScales,
  };
}

/** Extract [{ width, id }] from a variant's children attributes (de-duped by width). */
export function buildVariantWidthIdPairs(
  variant: Product | undefined | null,
): MaterialWidthIds[] {
  if (!variant?.children?.length) return [];
  const WIDTH_ID_ATTRIBUTE = 'afs-material';
  const widthMap: Record<string, string> = {};
  variant.children.forEach(({ attributes, masterAttributes }) => {
    const width =
      (masterAttributes?.width as string) ?? (attributes?.width as string);
    const widthId = attributes[WIDTH_ID_ATTRIBUTE] as string;
    if (width && widthId) widthMap[width] = widthId;
  });
  return Object.entries(widthMap).map(([width, id]) => ({ width, id }));
}
